import { BalanceData } from "../data/balance";
import { DailyRoadData } from "../data/dailyRoad";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { LevelModifiers } from "../sim/modifiers";
import { SECONDS_PER_DAY, utcDay } from "./orders";

/**
 * Daily Road (MP-20, docs/MODES.md §4): one road a UTC day, the same for every player on every device. Everything the
 * run is built from — the campaign layout, the stage count, the run seed (so the card offers), the raiders' power —
 * comes from hashes of the UTC day number and the data (modes.json `daily`); the towers, their levels and the absent
 * gear come from the normalised profile (meta/fairProfile.ts). Nothing here reads the player's save.
 */

/** Hash tags, apart from every other hash of a day. */
const MAP_TAG = "dailyRoadMaps", STAGES_TAG = "dailyRoadStages", SEED_TAG = "dailyRoadSeed";

/** The day's road: everything a Daily Road run of `day` is built from. */
export interface DailyRoad {
  /** The UTC day number (days since 1970-01-01). */
  readonly day: number;
  /** The campaign level whose layout (grid, road, groups, boss) the road borrows. */
  readonly level: number;
  readonly stages: number;
  /** The run's seed: the card offers and every roll of the run. */
  readonly seed: bigint;
  readonly modifiers: LevelModifiers;
}

function hashOf(tag: string, n: number): bigint {
  const h = new Fnv1a64();
  h.writeString(tag);
  h.writeInt(n);
  return h.value;
}

/** Cycle `c`'s order of the layout pool (offsets 0..n−1), a Fisher–Yates shuffle seeded by the cycle. */
function cycleOrder(c: number, n: number): number[] {
  const rng = new Pcg32(hashOf(MAP_TAG, c));
  const out = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = rng.nextInt(i + 1); [out[i], out[j]] = [out[j], out[i]]; }
  return out;
}

/**
 * The layout of `day`: the pool (mapFrom..mapTo) walked in a shuffled order, one shuffle per cycle of n days, so no
 * layout repeats within a cycle; a cycle that would open on the layout the previous one closed on swaps its first two.
 */
export function dailyMapLevel(data: DailyRoadData, day: number): number {
  const n = data.mapTo - data.mapFrom + 1;
  if (n <= 1) return data.mapFrom;
  const c = Math.floor(day / n), pos = day - c * n;
  const order = cycleOrder(c, n);
  if (n > 2 && order[0] === cycleOrder(c - 1, n)[n - 1]) [order[0], order[1]] = [order[1], order[0]];
  return data.mapFrom + order[pos];
}

/** The day's stage count, stagesMin..stagesMax. */
export function dailyStages(data: DailyRoadData, day: number): number {
  const span = data.stagesMax - data.stagesMin + 1;
  return data.stagesMin + Number(hashOf(STAGES_TAG, day) % BigInt(span));
}

/** The day's run seed. */
export function dailySeed(day: number): bigint { return hashOf(SEED_TAG, day); }

/** The raiders' health on the day's map: anchored at level `powerLevel` whatever the map, × enemyHpMult. */
export function dailyHpMult(balance: BalanceData, mapLevel: number): Fix64 {
  const d = balance.modes.daily, t = balance.tables;
  const anchor = t.levelBaseAt(Math.min(d.powerLevel, t.maxLevel));
  return anchor.div(t.levelBaseAt(mapLevel)).mul(d.enemyHpMult);
}

/** The road of UTC day `day`. Pure: the same day gives the same road everywhere. */
export function dailyRoad(balance: BalanceData, day: number): DailyRoad {
  const d = balance.modes.daily;
  const level = Math.min(dailyMapLevel(d, day), balance.levels.length);
  const stages = Math.min(dailyStages(d, day), balance.stagesOf(level));
  return { day, level, stages, seed: dailySeed(day), modifiers: { enemyHpMult: dailyHpMult(balance, level), daily: { day, stages } } };
}

/** Daily Road opens after level `unlockAfterLevel` (the Weekly Trial's). */
export function dailyUnlocked(data: DailyRoadData, highestCleared: number): boolean { return highestCleared >= data.unlockAfterLevel; }

/** When the road of `day` gives way to the next (00:00 UTC). */
export function dailyEndsAt(day: number): number { return (day + 1) * SECONDS_PER_DAY; }
/** Today's day number. */
export function dailyDay(now: number): number { return utcDay(now); }

/** What a finished run's score is read from. */
export interface DailyRunFacts { readonly won: boolean; readonly stagesCleared: number; readonly baseHp: number }

/** A run's score: stagePoints × stages cleared + hpPoints × Fortress HP left + winPoints on a win. Deterministic. */
export function dailyScore(data: DailyRoadData, r: DailyRunFacts): number {
  return data.stagePoints * Math.max(0, r.stagesCleared) + data.hpPoints * Math.max(0, r.baseHp) + (r.won ? data.winPoints : 0);
}

/** The proleptic Gregorian date of a UTC day number, by whole-number arithmetic: [year, month 1–12, day 1–31]. */
export function civilDate(day: number): [number, number, number] {
  const z = day + 719468, era = Math.floor(z / 146097), doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1, m = mp < 10 ? mp + 3 : mp - 9;
  return [yoe + era * 400 + (m <= 2 ? 1 : 0), m, d];
}

/** "2026-10-12": the day's label on the sheet, the history and the share card. */
export function dailyDateLabel(day: number): string {
  const [y, m, d] = civilDate(day);
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
